import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles } from '@material-ui/core'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'

// Sortable detail table for the scatter tool. Shows every row that
// went into the chart (including ones the chart dropped for having a
// null X or Y -- they're listed at the bottom with `--` placeholders
// so the user can see why a name they expected isn't on the chart).
//
// Sorting is local React state, defaults to "Y desc" so the most
// interesting points are at the top out of the gate. Clicking the
// already-active column flips direction. Name column sort is
// case-insensitive string sort.

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
    overflow: 'auto',
    borderTop: '1px solid var(--opal-border-default, #eee)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12
  },
  th: {
    textAlign: 'left',
    padding: '6px 10px',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-700, #555)',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    background: 'var(--opal-surface-default, #fff)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    cursor: 'pointer',
    userSelect: 'none',
    // The header is keyboard-focusable; make the focus state visible
    // so keyboard users can see which column they're about to sort.
    // Suppress the default outline only when the focus came from a
    // pointer (matches Material-UI's `focusVisible` convention).
    outline: 'none',
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-500, #1976d2)',
      outlineOffset: -2
    }
  },
  thInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2
  },
  thRight: {
    textAlign: 'right',
    '& $thInner': { flexDirection: 'row-reverse' }
  },
  sortIconBtn: {
    padding: 0,
    width: 14,
    height: 14,
    color: 'var(--color-neutral-700, #555)'
  },
  sortIcon: {
    fontSize: 12
  },
  td: {
    padding: '4px 10px',
    borderBottom: '1px solid var(--opal-border-default, #f3f3f3)',
    color: 'var(--color-neutral-800, #333)'
  },
  tdRight: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  tdNegative: { color: 'var(--color-danger-600, #c62828)' },
  dash: { color: 'var(--color-neutral-300, #ccc)' }
}))

function compareRows (a, b, sortBy) {
  if (sortBy === 'name') {
    return String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase())
  }
  const av = typeof a[sortBy] === 'number' ? a[sortBy] : null
  const bv = typeof b[sortBy] === 'number' ? b[sortBy] : null
  if (av === null && bv === null) return 0
  if (av === null) return 1 // nulls sink
  if (bv === null) return -1
  return av - bv
}

function sortIconFor (active, dir, classes) {
  if (!active) return null
  const Icon = dir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon
  return (
    <IconButton size='small' className={classes.sortIconBtn} disableRipple tabIndex={-1} aria-hidden>
      <Icon className={classes.sortIcon} />
    </IconButton>
  )
}

export default function ScatterDetailTable ({ rows, axisXLabel, axisYLabel, axisXFormat, axisYFormat }) {
  const classes = useStyles()
  const { formatter: fmt } = useFormattingContext()

  // Default sort: Y desc. "Most interesting" row at the top.
  const [sortBy, setSortBy] = useState('y')
  const [sortDir, setSortDir] = useState('desc')

  const sorted = useMemo(() => {
    const copy = [...(rows || [])]
    copy.sort((a, b) => {
      const cmp = compareRows(a, b, sortBy)
      return sortDir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [rows, sortBy, sortDir])

  function toggleSort (next) {
    if (next === sortBy) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(next)
      setSortDir(next === 'name' ? 'asc' : 'desc')
    }
  }

  function thProps (key, isRight) {
    const active = sortBy === key
    return {
      className: `${classes.th} ${isRight ? classes.thRight : ''}`,
      // Keyboard-accessible sort. The whole `<th>` is the focusable
      // target (single Tab stop per column); inner sort indicator
      // IconButton is `tabIndex=-1` so it doesn't steal focus. Enter
      // and Space both trigger the same toggle the mouse onClick does.
      role: 'columnheader',
      tabIndex: 0,
      onClick: () => toggleSort(key),
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          toggleSort(key)
        }
      },
      'aria-sort': active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
    }
  }

  return (
    <div className={classes.wrapper}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th {...thProps('name', false)}>
              <span className={classes.thInner}>
                <span>Name</span>
                {sortIconFor(sortBy === 'name', sortDir, classes)}
              </span>
            </th>
            <th {...thProps('x', true)}>
              <span className={classes.thInner}>
                <span>{axisXLabel || 'X'}</span>
                {sortIconFor(sortBy === 'x', sortDir, classes)}
              </span>
            </th>
            <th {...thProps('y', true)}>
              <span className={classes.thInner}>
                <span>{axisYLabel || 'Y'}</span>
                {sortIconFor(sortBy === 'y', sortDir, classes)}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const xNeg = typeof r.x === 'number' && r.x < 0
            const yNeg = typeof r.y === 'number' && r.y < 0
            return (
              <tr key={r.groupId ?? r.name}>
                <td className={classes.td}>{r.name}</td>
                <td className={`${classes.td} ${classes.tdRight} ${xNeg ? classes.tdNegative : ''}`}>
                  {typeof r.x === 'number' ? fmt(r.x, axisXFormat) : <span className={classes.dash}>--</span>}
                </td>
                <td className={`${classes.td} ${classes.tdRight} ${yNeg ? classes.tdNegative : ''}`}>
                  {typeof r.y === 'number' ? fmt(r.y, axisYFormat) : <span className={classes.dash}>--</span>}
                </td>
              </tr>
            )
          })}
          {sorted.length === 0 && (
            <tr>
              <td className={classes.td} colSpan={3}>
                <span className={classes.dash}>No data</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

ScatterDetailTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    x: PropTypes.number,
    y: PropTypes.number
  })).isRequired,
  axisXLabel: PropTypes.string,
  axisYLabel: PropTypes.string,
  axisXFormat: PropTypes.string,
  axisYFormat: PropTypes.string
}
